import TrackArtistCredits from "./TrackArtistCredits";
import BpmInput from "../ui/BpmInput";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import MenuSurface from "../ui/MenuSurface";
import MenuItem from "@mui/material/MenuItem";
import useEditorForm from "../hooks/useEditorForm";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SelectInput from "../ui/SelectInput";
import TextInput from "../ui/TextInput";
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import TagDropdown from "./TagDropdown";
import useAsyncAction from "../hooks/useAsyncAction";
import { describeApiError } from "../api/request";
import { trackFlagText, normalizeBreaks, normalizeTags } from "../utils";
function TrackRowReadOnly({
  readOnly = false,
  track,
  role,
  onSave,
  canEditDuration,
  canEditTrackInfo,
  highlight,
  tagOptions = null,
  onCreateTag,
  breakOptions = [],
}) {
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const action = useAsyncAction();
  const tagSelectionEnabled = tagOptions !== null;
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const makeFields = React.useCallback(
    (t) => ({
      position: t.position || "",
      title: t.title || "",
      duration: t.duration || "",
      bpm: t.bpm ?? "",
      rating: t.love ? "love" : t.like ? "like" : t.hate ? "hate" : "",
      breaks: normalizeBreaks(t.breaks),
      notes: t.notes || "",
      tags: tagSelectionEnabled ? normalizeTags(t.tags) : t.tags || "",
    }),
    [tagSelectionEnabled],
  );
  const [editing, setEditing] = React.useState(false);
  const [fields, setFields, editorForm] = useEditorForm(makeFields(track));
  React.useEffect(() => {
    setFields((previous) =>
      editing
        ? {
            ...previous,
            tags: tagSelectionEnabled
              ? normalizeTags(previous.tags)
              : Array.isArray(previous.tags)
                ? previous.tags.join(", ")
                : previous.tags,
          }
        : makeFields(track),
    );
  }, [track, makeFields, editing, tagSelectionEnabled, setFields]);
  const handleMenuToggle = (e) => {
    if (menuOpen) {
      e.currentTarget.blur();
    }
    setMenuOpen(!menuOpen);
  };
  const toggleField = async (field) => {
    await action.run(() =>
      onSave(track.id, {
        [field]: !track[field],
      }),
    );
    setMenuOpen(false);
  };
  const handleChange = (field, value) => {
    setFields((f) => ({
      ...f,
      [field]: value,
    }));
  };
  const handleSave = async () => {
    const data = {
      ...(canEditDuration
        ? {
            duration: fields.duration,
          }
        : {}),
      ...(canEditTrackInfo
        ? {
            title: fields.title,
            position: fields.position,
          }
        : {}),
      bpm: fields.bpm,
      like: fields.rating === "like",
      love: fields.rating === "love",
      hate: fields.rating === "hate",
      notes: fields.notes,
      tags: tagOptions ? fields.tags.join(", ") : fields.tags,
    };
    if (Array.isArray(fields.breaks)) {
      data.breaks = fields.breaks;
    }
    if (await action.run(() => onSave(track.id, data))) {
      setEditing(false);
      setMenuOpen(false);
    }
  };
  const handleCancel = () => {
    action.clearError();
    setFields(makeFields(track));
    setEditing(false);
    setMenuOpen(false);
  };
  return (
    <TableRow
      className={`track${highlight ? " highlight" : ""}`}
      aria-busy={action.pending}
    >
      {editing ? (
        <>
          <TableCell>
            {canEditTrackInfo ? (
              <TextInput
                type="text"
                value={fields.position}
                aria-label="Position"
                disabled={action.pending}
                onChange={(e) => handleChange("position", e.target.value)}
              />
            ) : (
              track.position
            )}
          </TableCell>
          <TableCell>
            {canEditTrackInfo ? (
              <TextInput
                className="title-input"
                value={fields.title}
                aria-label="Track title"
                disabled={action.pending}
                onChange={(e) => handleChange("title", e.target.value)}
                multiline
              />
            ) : (
              track.title
            )}
          </TableCell>
          <TableCell>
            {canEditDuration ? (
              <TextInput
                type="text"
                value={fields.duration}
                aria-label="Duration"
                disabled={action.pending}
                onChange={(e) => handleChange("duration", e.target.value)}
              />
            ) : (
              track.duration
            )}
          </TableCell>
          <TableCell>
            <BpmInput
              value={fields.bpm}
              aria-label="BPM"
              disabled={action.pending}
              onChange={(e) => handleChange("bpm", e.target.value)}
            />
          </TableCell>
          <TableCell>
            <SelectInput
              value={fields.rating}
              aria-label="Rating"
              disabled={action.pending}
              onChange={(e) => handleChange("rating", e.target.value)}
            >
              <option value=""></option>
              <option value="like">👍</option>
              <option value="love">❤️</option>
              <option value="hate">❌</option>
            </SelectInput>
          </TableCell>
          <TableCell>
            <TextInput
              className="notes-input"
              value={fields.notes}
              aria-label="Notes"
              disabled={action.pending}
              onChange={(e) => handleChange("notes", e.target.value)}
              multiline
            />
          </TableCell>
          <TableCell>
            {tagOptions ? (
              <TagDropdown
                disabled={action.pending}
                options={tagOptions}
                selected={fields.tags}
                onChange={(opts) => handleChange("tags", opts)}
                onCreate={onCreateTag}
              />
            ) : (
              <TextInput
                type="text"
                value={fields.tags}
                aria-label="Tags"
                disabled={action.pending}
                onChange={(e) => handleChange("tags", e.target.value)}
              />
            )}
          </TableCell>
          <TableCell className="track-row-actions">
            {action.error && (
              <Alert role="alert" severity="error">
                {describeApiError(action.error)}
              </Alert>
            )}
            <div className="menu track-menu" ref={menuRef}>
              <IconButton
                type="button"
                className="menu-toggle"
                onClick={(event) => {
                  setMenuAnchor(event.currentTarget);
                  handleMenuToggle(event);
                }}
                aria-label="Track options"
                aria-expanded={menuOpen}
                disabled={action.pending}
                aria-haspopup="menu"
              >
                <MoreVertIcon />
              </IconButton>
              {menuOpen && (
                <MenuSurface
                  anchorEl={menuAnchor}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  fitBelow
                  onClose={() => setMenuOpen(false)}
                >
                  <MenuItem
                    type="button"
                    onClick={editorForm.handleSubmit(handleSave)}
                    disabled={action.pending}
                    component="button"
                  >
                    {action.pending ? "Saving…" : "Save Edit"}
                  </MenuItem>
                  <MenuItem
                    type="button"
                    onClick={handleCancel}
                    disabled={action.pending}
                    component="button"
                  >
                    Cancel Edit
                  </MenuItem>
                </MenuSurface>
              )}
            </div>
          </TableCell>
        </>
      ) : (
        <>
          <TableCell>{track.position}</TableCell>
          <TableCell>
            {track.title}
            {trackFlagText(track) && (
              <div className="track-subtitle">{trackFlagText(track)}</div>
            )}
            <TrackArtistCredits track={track} readOnly={readOnly} />
          </TableCell>
          <TableCell>{track.duration}</TableCell>
          <TableCell
            className={
              track.bpm == null && track.default_bpm != null
                ? "default-bpm"
                : ""
            }
          >
            {track.bpm ?? track.default_bpm}
          </TableCell>
          <TableCell>
            {track.love ? "❤️" : track.like ? "👍" : track.hate ? "❌" : ""}
          </TableCell>
          <TableCell>{track.notes}</TableCell>
          <TableCell>{track.tags}</TableCell>
          <TableCell className="track-row-actions">
            {!readOnly && (
              <>
                {action.error && (
                  <Alert role="alert" severity="error">
                    {describeApiError(action.error)}
                  </Alert>
                )}
                <div className="menu track-menu" ref={menuRef}>
                  <IconButton
                    type="button"
                    className="menu-toggle"
                    onClick={(event) => {
                      setMenuAnchor(event.currentTarget);
                      handleMenuToggle(event);
                    }}
                    aria-label="Track options"
                    aria-expanded={menuOpen}
                    disabled={action.pending}
                    aria-haspopup="menu"
                  >
                    <MoreVertIcon />
                  </IconButton>
                  {menuOpen && (
                    <MenuSurface
                      anchorEl={menuAnchor}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                      fitBelow
                      onClose={() => setMenuOpen(false)}
                    >
                      <MenuItem
                        onClick={() => {
                          setEditing(true);
                          setMenuOpen(false);
                        }}
                        component="button"
                      >
                        Edit Track
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          const ret = encodeURIComponent(
                            location.pathname + location.search,
                          );
                          navigate(
                            `/add-to-set?track=${track.id}&return=${ret}`,
                          );
                          setMenuOpen(false);
                        }}
                        component="button"
                      >
                        Add To Set
                      </MenuItem>
                      {(role === "admin" || role === "user") && (
                        <>
                          <MenuItem
                            onClick={() => toggleField("is_instrumental")}
                            component="button"
                          >
                            Set Instrumental{" "}
                            {track.is_instrumental ? "False" : "True"}
                          </MenuItem>
                          <MenuItem
                            onClick={() => toggleField("is_a_cappella")}
                            component="button"
                          >
                            Set A Cappella{" "}
                            {track.is_a_cappella ? "False" : "True"}
                          </MenuItem>
                        </>
                      )}
                    </MenuSurface>
                  )}
                </div>
              </>
            )}
          </TableCell>
        </>
      )}
    </TableRow>
  );
}
export default TrackRowReadOnly;
